import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { invoiceApi, printApi } from "../../api/lab";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Page, Button, Badge, Table, Pager, Tabs, Stat, Bars, Section, Loading } from "../../Components/UI/ui";
import { GATE, INVOICE_STATUSES, money, fmtDate, todayStr, addDays, openPdf } from "../../lib/lab";
import InvoiceDetail from "./InvoiceDetail";
import WalkInInvoice from "./WalkInInvoice";

const Billing = () => {
  const { can } = useAuth();
  const { toast, branding, loadBranding, defaultLayout } = useApp();
  const navigate = useNavigate();
  const admin = can(...GATE.billingAdmin);
  const [tab, setTab] = useState("invoices");
  const [status, setStatus] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [startDate, setStartDate] = useState(addDays(todayStr(), -30));
  const [endDate, setEndDate] = useState(todayStr());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ invoices: [], totalCount: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [walkIn, setWalkIn] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25, startDate, endDate };
      if (status) params.status = status;
      if (overdue) params.overdue = true;
      if (search.trim()) params.search = search.trim();
      setData(await invoiceApi.list(params));
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [page, startDate, endDate, status, overdue, search, toast]);

  const loadSummary = useCallback(async () => {
    if (!admin) return;
    setSumLoading(true);
    try { setSummary(await invoiceApi.summary({ startDate, endDate })); }
    catch (e) { toast.error(e); } finally { setSumLoading(false); }
  }, [admin, startDate, endDate, toast]);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);
  useEffect(() => { if (tab === "summary") loadSummary(); }, [tab, loadSummary]);
  useEffect(() => { loadBranding(); }, [loadBranding]);
  const gstinMissing = branding && !branding.branding?.invoice?.gstin;
  useEffect(() => { setPage(1); }, [status, overdue, startDate, endDate, search]);

  const columns = [
    { key: "invoiceNumber", title: "Invoice", render: (r) => (<><div className="primary">{r.invoiceNumber}</div><div className="sub">{fmtDate(r.date)}</div></>) },
    { key: "patientName", title: "Patient", render: (r) => (<><div>{r.patientName}</div><div className="sub">{r.patientPhone}</div></>) },
    { key: "items", title: "Items", render: (r) => <span className="sub">{(r.expenseTable || []).map((l) => l.name).join(", ")}</span> },
    { key: "grandTotal", title: "Total", align: "right", render: (r) => money(r.grandTotal) },
    { key: "totalPaidAmount", title: "Paid", align: "right", render: (r) => money(r.totalPaidAmount || 0) },
    { key: "outstanding", title: "Due", align: "right", render: (r) => <b>{money(r.outstanding ?? r.grandTotal - (r.totalPaidAmount || 0))}</b> },
    { key: "status", title: "Status", render: (r) => (<span className="lab-row">
        <Badge kind={r.status?.split(" ")[0]}>{r.status}</Badge>
        {r.isOverdue && !["Paid", "Cancelled"].includes(r.status) && <Badge kind="unpaid">Overdue</Badge>}
      </span>) },
    { key: "print", title: "", render: (r) => <Button size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); openPdf(printApi.invoicePdfUrl(r._id, { layout: defaultLayout })); }}>🖨</Button> },
  ];

  return (
    <Page
      title="Billing"
      actions={can(...GATE.invoiceWrite) && <Button variant="primary" onClick={() => setWalkIn(true)}><IoMdAdd /> Walk-in invoice</Button>}
    >
      {admin && <Tabs tabs={[{ id: "invoices", label: "Invoices" }, { id: "summary", label: "Summary" }]} value={tab} onChange={setTab} />}
      {gstinMissing && admin && (
        <div className="lab-muted">Add your GSTIN in <button type="button" className="lab-link" onClick={() => navigate("/setting?tab=letterhead")}>Settings → Letterhead</button> to print tax invoices.</div>
      )}

      <div className="lab-toolbar">
        {tab === "invoices" && <input className="lab-input grow" placeholder="Invoice number, patient name or phone" value={search} onChange={(e) => setSearch(e.target.value)} />}
        <input type="date" className="lab-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="lab-muted">to</span>
        <input type="date" className="lab-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        {tab === "invoices" && (
          <>
            <select className="lab-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="lab-check"><input type="checkbox" checked={overdue} onChange={(e) => setOverdue(e.target.checked)} /> Overdue only</label>
          </>
        )}
      </div>

      {tab === "invoices" ? (
        <>
          <div className="lab-page__body">
            <Table columns={columns} rows={data.invoices || []} loading={loading} onRowClick={(r) => setSelected(r._id)} empty="No invoices in this range" />
          </div>
          <Pager page={page} totalPages={data.totalPages} total={data.totalCount} onChange={setPage} />
        </>
      ) : (
        <div className="lab-page__body">
          {sumLoading || !summary ? <Loading /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="lab-stats">
                <Stat accent label="Invoiced" value={money(summary.invoiced)} sub={`${summary.invoices} invoices`} />
                <Stat label="Collected" value={money(summary.collected)} />
                <Stat label="Outstanding" value={money(summary.outstanding)} />
                <Stat label="Overdue" value={money(summary.overdue?.amount)} sub={`${summary.overdue?.count ?? 0} invoices`} />
              </div>
              <div className="lab-grid cols-3">
                <Section title="By status">
                  <Bars items={INVOICE_STATUSES.map((s) => ({ label: `${s} (${summary.byStatus?.[s]?.count ?? 0})`, value: summary.byStatus?.[s]?.amount ?? 0 }))} format={money} />
                </Section>
                <Section title="Payment methods">
                  <Bars color="y" items={(summary.paymentSplit || []).map((p) => ({ label: `${p.method} (${p.count})`, value: p.amount }))} format={money} />
                </Section>
                <Section title="Top services">
                  <Bars items={(summary.topServices || []).map((t) => ({ label: `${t.name} ×${t.quantity}`, value: t.revenue }))} format={money} />
                </Section>
              </div>
            </div>
          )}
        </div>
      )}

      <InvoiceDetail id={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={() => { load(); if (tab === "summary") loadSummary(); }} />
      <WalkInInvoice open={walkIn} onClose={() => setWalkIn(false)} onSaved={(inv) => { toast.success(`Invoice ${inv.invoiceNumber} created`); load(); setSelected(inv._id); }} />
    </Page>
  );
};

export default Billing;
